import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { Box, Collapse, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq/useHauserEQResults';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { useHOAChecklist } from '@/hooks/useHOAChecklist';
import { useMarketInsights } from '@/hooks/useMarketInsights';
import {
  DocumentClassificationType,
  DocumentRecord,
  DocumentRecordType,
} from '@/models/DocumentRecord';
import { VALUE_FONT, VALUE_TRACKING, VALUE_WEIGHT } from '@/theme/consts';

// Complete manual workflow: reading every supplied page plus annotations,
// cross-referencing/research, notes, client summaries, discussion and follow-up.
// Calibrated to the documented 24–47 hour all-in estimate for a 300-page packet.
const MANUAL_MIN_BASE_MINUTES = 10 * 60;
const MANUAL_MAX_BASE_MINUTES = 25 * 60;
const MANUAL_MIN_READING_MINUTES_PER_PAGE = 2.8;
const MANUAL_MAX_READING_MINUTES_PER_PAGE = 4.4;
// General AI-assisted workflow: upload/prompting, output verification,
// reconciling documents, notes and client communications. Calibrated to the
// documented 3–8 hour estimate for the same 300-page packet.
const AI_ASSISTED_MIN_BASE_MINUTES = 60;
const AI_ASSISTED_MAX_BASE_MINUTES = 2 * 60;
const AI_ASSISTED_MIN_VARIABLE_MINUTES_PER_PAGE = 0.4;
const AI_ASSISTED_MAX_VARIABLE_MINUTES_PER_PAGE = 1.2;
const MINUTES_PER_HOUR = 60;

export interface DocumentStats {
  documentCount: number;
  totalPages: number;
  analyzedPages: number;
}

const RECEIPT_ONLY_TYPES = new Set<DocumentClassificationType>([
  DocumentClassificationType.HOA_BYLAWS,
  DocumentClassificationType.HOA_ARTICLES_OF_INCORPORATION,
  DocumentClassificationType.HOA_FINANCIAL_STATEMENT,
  DocumentClassificationType.HOA_AGE_RESTRICTION,
  DocumentClassificationType.HOA_RENTAL_RESTRICTION,
  DocumentClassificationType.HOA_ASSESSMENT_CHANGE_NOTICE,
  DocumentClassificationType.HOA_TRANSFER_DISCLOSURE_FORM,
  DocumentClassificationType.HOA_CLOSING_INFORMATION,
  DocumentClassificationType.HOA_COMMON_AREA_DEFECT_LIST,
  DocumentClassificationType.HOA_COMMON_AREA_DEFECT_SETTLEMENT,
  DocumentClassificationType.HOA_EEE_INSPECTION_REPORT,
  DocumentClassificationType.HOA_VIOLATION_NOTICE,
]);

/** Counts only file records (excludes folder nodes) and sums their page
 *  counts — documents uploaded before pageCount existed contribute 0. */
export function computeDocumentStats(records: DocumentRecord[]): DocumentStats {
  const files = records.filter((r) => r.type === DocumentRecordType.File);
  const isReceiptOnly = (r: DocumentRecord) =>
    RECEIPT_ONLY_TYPES.has(r.documentType as DocumentClassificationType);
  return {
    documentCount: files.length,
    totalPages: files.reduce((sum, r) => sum + (r.pageCount ?? 0), 0),
    analyzedPages: files.reduce((sum, r) => sum + (isReceiptOnly(r) ? 0 : r.pageCount ?? 0), 0),
  };
}

/** Formats a [min, max] minute range as a human string, in whichever unit
 *  (minutes or hours) the upper bound falls into — "15–25 min", "3–6 hrs". */
export function formatMinutesRange(minMinutes: number, maxMinutes: number): string {
  if (maxMinutes < MINUTES_PER_HOUR) {
    const lo = Math.max(5, Math.round(minMinutes / 5) * 5);
    const hi = Math.max(5, Math.round(maxMinutes / 5) * 5);
    return lo === hi ? `${lo} min` : `${lo}–${hi} min`;
  }
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const lo = Math.round((minMinutes / MINUTES_PER_HOUR) * 2) / 2;
  const hi = Math.round((maxMinutes / MINUTES_PER_HOUR) * 2) / 2;
  return lo === hi ? `${fmt(lo)} hrs` : `${fmt(lo)}–${fmt(hi)} hrs`;
}

export interface HauserReviewWorkload {
  reviewableWords: number;
  dataPoints: number;
  reviewCards: number;
  actionableFindings: number;
}

const REVIEW_METADATA_KEYS = new Set([
  'id',
  'propertyId',
  'documentRecordId',
  'documentRecordIds',
  'source',
  'extractedAt',
  'attomId',
]);

export function measureReviewContent(value: unknown): { words: number; dataPoints: number } {
  if (typeof value === 'string') {
    return { words: value.trim() ? value.trim().split(/\s+/).length : 0, dataPoints: 0 };
  }
  if (typeof value === 'number' || typeof value === 'boolean') return { words: 0, dataPoints: 1 };
  if (Array.isArray(value)) {
    return value.reduce(
      (total, item) => {
        const measured = measureReviewContent(item);
        return {
          words: total.words + measured.words,
          dataPoints: total.dataPoints + measured.dataPoints,
        };
      },
      { words: 0, dataPoints: 0 },
    );
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce(
      (total, [key, item]) => {
        if (REVIEW_METADATA_KEYS.has(key)) return total;
        const measured = measureReviewContent(item);
        return {
          words: total.words + measured.words,
          dataPoints: total.dataPoints + measured.dataPoints,
        };
      },
      { words: 0, dataPoints: 0 },
    );
  }
  return { words: 0, dataPoints: 0 };
}

/** Human-time model for the actual content shown by Hauser. Structured cards
 * are read at advanced-to-careful screen speed (300–200 wpm), with separate
 * time for interpreting data, moving through review surfaces, taking and
 * validating notes, client communication and follow-up. */
export function estimateHauserReviewMinutes(workload: HauserReviewWorkload): [number, number] {
  return [
    6 +
      workload.reviewableWords / 300 +
      workload.dataPoints * 0.01 +
      workload.reviewCards * 0.05 +
      workload.actionableFindings * 0.15 +
      20,
    10 +
      workload.reviewableWords / 200 +
      workload.dataPoints * 0.025 +
      workload.reviewCards * 0.1 +
      workload.actionableFindings * 0.35 +
      38,
  ];
}

export function estimateManualReviewMinutes(totalPages: number): [number, number] {
  return [
    MANUAL_MIN_BASE_MINUTES + totalPages * MANUAL_MIN_READING_MINUTES_PER_PAGE,
    MANUAL_MAX_BASE_MINUTES + totalPages * MANUAL_MAX_READING_MINUTES_PER_PAGE,
  ];
}

export function estimateGeneralAiReviewMinutes(totalPages: number): [number, number] {
  return [
    AI_ASSISTED_MIN_BASE_MINUTES + totalPages * AI_ASSISTED_MIN_VARIABLE_MINUTES_PER_PAGE,
    AI_ASSISTED_MAX_BASE_MINUTES + totalPages * AI_ASSISTED_MAX_VARIABLE_MINUTES_PER_PAGE,
  ];
}

// Matches the app's established eyebrow convention (RentVsBuyCalculator's
// .rvb-eyebrow: 11px/700/0.3em tracking/uppercase), shrunk down for this
// denser card context rather than the 11px original.
const STAT_LABEL_STYLE = {
  fontFamily: 'Manrope, sans-serif',
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--tx-faint)',
};

const STAT_VALUE_STYLE = {
  fontFamily: 'Manrope, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--tx-text)',
};

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Counts up from 0 to `target` on mount (ease-out, ~900ms) — used for the
 *  triptych's onload number ramp. Skips straight to target if the user
 *  prefers reduced motion. */
function useCountUp(target: number, durationMs = 900): number {
  const [value, setValue] = useState(() => (prefersReducedMotion() ? target : 0));

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }
    let rafId: number;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, durationMs]);

  return value;
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '6px' }}>
      <Typography sx={STAT_LABEL_STYLE}>{label}</Typography>
      <Typography sx={STAT_VALUE_STYLE}>{value}</Typography>
    </Box>
  );
}

// Shared card shell — both StatTile and AnalyzedDonutTile use this so the
// three triptych items line up on identical padding/height/alignment
// (grid stretches all three to the tallest one; centering the content of
// each is what makes them read as vertically aligned with each other).
const STAT_TILE_CARD_STYLE = {
  background: 'var(--tx-fill)',
  borderRadius: '12px',
  p: '14px 10px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
};

// The value/donut zone gets the remaining height and centers its content
// within it; the label stays pinned at the bottom of the card. Across all
// three tiles (equal height via the grid's default stretch), this puts every
// label on the same baseline while still centering each value in the space
// above it, rather than centering label+value as one block.
const STAT_TILE_VALUE_ZONE_STYLE = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Static hour/minute hands, one thin second hand animated counter-clockwise
// (time "running backward") — evokes the "hours given back" framing this
// icon sits next to. Respects prefers-reduced-motion.
function ReverseClock({
  size = 40,
  hourColor = '#6b6b6b',
  minuteColor = '#C0FF02',
}: {
  size?: number;
  hourColor?: string;
  minuteColor?: string;
}) {
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <style>{`
          @keyframes reverseClockTick {
            from { transform: rotate(360deg); }
            to { transform: rotate(0deg); }
          }
          .reverse-clock-hand {
            transform-origin: 12px 12px;
            animation-name: reverseClockTick;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
          }
          /* Hour hand runs 12x slower than the minute hand, same ratio a
             real clock face uses, just counter-clockwise. */
          .reverse-clock-hour-hand { animation-duration: 72s; }
          /* Pendulum-style motion: slowest at 12 (both the start and end of
             each cycle), fastest through the middle of the sweep. Never a
             dead stop, and the low velocity at both boundaries means each
             loop blends into the next with no visible jump. */
          @keyframes reverseClockMinuteRamp {
            0% { transform: rotate(360deg); }
            100% { transform: rotate(0deg); }
          }
          .reverse-clock-minute-hand {
            animation-name: reverseClockMinuteRamp;
            animation-duration: 2s;
            animation-timing-function: ease-in-out;
          }
          @media (prefers-reduced-motion: reduce) {
            .reverse-clock-hand { animation: none; }
          }
        `}</style>
        <circle cx="12" cy="12" r="9" stroke={hourColor} strokeWidth="1.5" />
        {/* Drawn before the hour hand, so it renders underneath it (SVG
            paints in document order — later elements sit on top). */}
        <line
          className="reverse-clock-hand reverse-clock-minute-hand"
          x1="12"
          y1="12"
          x2="12"
          y2="6.5"
          stroke={minuteColor}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <line
          className="reverse-clock-hand reverse-clock-hour-hand"
          x1="12"
          y1="12"
          x2="12"
          y2="8"
          stroke={hourColor}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
}

function StatTile({ value, label }: { value: number; label: string }) {
  const animatedValue = useCountUp(value);
  return (
    <Box sx={STAT_TILE_CARD_STYLE}>
      <Box sx={STAT_TILE_VALUE_ZONE_STYLE}>
        <Typography
          sx={{
            fontFamily: VALUE_FONT,
            letterSpacing: VALUE_TRACKING,
            fontSize: 32,
            lineHeight: 1,
            fontWeight: VALUE_WEIGHT,
            color: 'var(--tx-text)',
          }}>
          {animatedValue}
        </Typography>
      </Box>
      <Typography sx={{ ...STAT_LABEL_STYLE, color: 'var(--tx-muted)', mt: '6px' }}>{label}</Typography>
    </Box>
  );
}

function AnalyzedDonutTile({ percent }: { percent: number }) {
  const animatedPercent = useCountUp(percent);
  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  // Ring fill mirrors the counter — driven by the same animated value each
  // frame, rather than a separate CSS transition, so the number and the
  // ring always land on 0% together and finish together.
  const offset = circumference * (1 - animatedPercent / 100);

  return (
    <Box sx={STAT_TILE_CARD_STYLE}>
      <Box sx={STAT_TILE_VALUE_ZONE_STYLE}>
        <Box sx={{ position: 'relative', width: 56, height: 56 }}>
          <svg width="56" height="56" viewBox="0 0 56 56">
            <circle
              cx="28"
              cy="28"
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,.08)"
              strokeWidth={4}
            />
            <circle
              cx="28"
              cy="28"
              r={radius}
              fill="none"
              stroke="#C0FF02"
              strokeWidth={4}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 28 28)"
            />
          </svg>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography
              sx={{ fontFamily: VALUE_FONT, fontSize: 17, fontWeight: 600, color: 'var(--tx-text)' }}>
              {animatedPercent}%
            </Typography>
          </Box>
        </Box>
      </Box>
      <Typography sx={{ ...STAT_LABEL_STYLE, color: 'var(--tx-muted)', mt: '6px' }}>Analyzed</Typography>
    </Box>
  );
}

/** Splits a formatted "15–34 hrs" / "45 min" string into its numeric part
 *  and unit, so the unit can render as a smaller suffix beside the value
 *  instead of being folded into (or stripped from) the big number itself. */
function splitValueUnit(formatted: string): [string, string] {
  const match = formatted.match(/^(.+?)\s(hrs|min)$/);
  return match ? [match[1], match[2]] : [formatted, ''];
}

function SuffixStat({ value, suffix, label }: { value: string; suffix: string; label: string }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', whiteSpace: 'nowrap' }}>
        <Typography
          sx={{
            fontFamily: VALUE_FONT,
            letterSpacing: VALUE_TRACKING,
            fontSize: 17,
            lineHeight: 1,
            fontWeight: VALUE_WEIGHT,
            color: 'var(--tx-text)',
            whiteSpace: 'nowrap',
          }}>
          {value}
        </Typography>
        {suffix && (
          <Typography
            sx={{
              fontFamily: VALUE_FONT,
              letterSpacing: VALUE_TRACKING,
              fontSize: 10,
              fontWeight: VALUE_WEIGHT,
              color: 'var(--tx-muted)',
              whiteSpace: 'nowrap',
            }}>
            {suffix}
          </Typography>
        )}
      </Box>
      <Typography sx={{ ...STAT_LABEL_STYLE, color: 'var(--tx-muted)', fontSize: 10, mt: '6px' }}>
        {label}
      </Typography>
    </Box>
  );
}

type WorkflowEstimate = {
  key: 'manual' | 'ai' | 'hauser';
  label: string;
  color: string;
  range: [number, number];
};

function hoursReturned(manual: [number, number], hauser: [number, number]): [number, number] {
  return [Math.max(0, manual[0] - hauser[1]), Math.max(0, manual[1] - hauser[0])];
}

function formatWorkflowRange(workflow: WorkflowEstimate): string {
  return formatMinutesRange(...workflow.range);
}

function estimateWorkflows(totalPages: number, workload: HauserReviewWorkload): WorkflowEstimate[] {
  return [
    {
      key: 'manual',
      label: 'Manual, all-in',
      color: 'var(--tx-muted)',
      range: estimateManualReviewMinutes(totalPages),
    },
    {
      key: 'ai',
      label: 'General AI-assisted',
      color: 'var(--tx-text)',
      range: estimateGeneralAiReviewMinutes(totalPages),
    },
    {
      key: 'hauser',
      label: 'Hauser, all-in',
      color: 'var(--tx-lime-ink)',
      range: estimateHauserReviewMinutes(workload),
    },
  ];
}

function WorkflowBars({ workflows }: { workflows: WorkflowEstimate[] }) {
  // Linear, not log — a log scale compresses the Manual-vs-Hauser gap, which
  // is the entire point of this comparison, into something much smaller
  // than it actually is. Bar length here is directly proportional to
  // minutes, all three measured against the same ceiling (Manual's max).
  const scaleMax = workflows[0].range[1];
  const position = (minutes: number) => (minutes / scaleMax) * 100;

  return (
    <Box sx={{ display: 'grid', gap: '13px', mt: '20px' }}>
      {workflows.map((workflow) => {
        // Real bar, grown from zero — not a floating min/max segment. The
        // solid portion covers the low end of the range, the lighter
        // continuation out to the high end, so the bar still reads as one
        // shape starting at the left edge instead of hovering mid-track.
        const minWidth = position(workflow.range[0]);
        const maxWidth = position(workflow.range[1]);
        return (
          <Box key={workflow.key}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: '6px',
              }}>
              <Typography sx={{ ...STAT_LABEL_STYLE, color: 'var(--tx-muted)' }}>
                {workflow.label}
              </Typography>
              <Typography sx={{ ...STAT_LABEL_STYLE, color: 'var(--tx-text)', letterSpacing: 0 }}>
                {formatWorkflowRange(workflow)}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 7,
                borderRadius: 999,
                background: 'var(--tx-fill-2)',
                position: 'relative',
                overflow: 'hidden',
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  width: `${Math.max(2, maxWidth)}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: workflow.color,
                  opacity: 0.35,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  width: `${Math.max(2, minWidth)}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: workflow.color,
                  boxShadow: workflow.key === 'hauser' ? '0 0 12px rgba(192,255,2,0.35)' : 'none',
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function EfficiencyComparison({
  totalPages,
  workload,
}: {
  totalPages: number;
  workload: HauserReviewWorkload;
}) {
  const workflows = estimateWorkflows(totalPages, workload);
  const returned = hoursReturned(workflows[0].range, workflows[2].range);
  const returnedRange = formatMinutesRange(...returned);
  const efficiencyMin = Math.max(
    0,
    Math.round((1 - workflows[2].range[1] / workflows[0].range[0]) * 100),
  );
  const efficiencyMax = Math.max(
    0,
    Math.round((1 - workflows[2].range[0] / workflows[0].range[1]) * 100),
  );

  const [returnedValue, returnedUnit] = splitValueUnit(returnedRange);

  return (
    <Box
      data-review-words={import.meta.env.DEV ? workload.reviewableWords : undefined}
      data-review-points={import.meta.env.DEV ? workload.dataPoints : undefined}
      data-review-cards={import.meta.env.DEV ? workload.reviewCards : undefined}
      data-review-findings={import.meta.env.DEV ? workload.actionableFindings : undefined}
      sx={{ mt: '30px', pt: '25px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '17px' }}>
        <ReverseClock />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', flex: 1 }}>
          <SuffixStat value={returnedValue} suffix={returnedUnit} label="Hours saved" />
          <SuffixStat
            value={`${efficiencyMin}–${efficiencyMax}`}
            suffix="%"
            label="Faster review"
          />
        </Box>
      </Box>

      <WorkflowBars workflows={workflows} />
      <Typography sx={{ fontSize: 10, lineHeight: 1.45, color: '#595959', mt: '14px' }}>
        Includes review, notes, validation, client communication and follow-up. Receipt-only pages
        are counted on file but excluded from Hauser-analyzed volume.
      </Typography>
    </Box>
  );
}

/** Pinned footer for ChatPanel's Tasks sub-tab — a collapsed-by-default
 *  summary of due-diligence volume for the currently-selected property,
 *  scoped to that property only (not an aggregate across all properties). */
export function PropertyStatsFooter() {
  const [expanded, setExpanded] = useState(false);
  const { selectedProperty, propertyIssues } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { documentRecords } = useDocumentRecords(propertyId);
  const { contextSubfactorExplanations } = useHauserEQResults(propertyId);
  const { data: hoaData } = useHOAChecklist(propertyId);
  const { data: marketData } = useMarketInsights(propertyId);

  if (!propertyId) return null;

  const { documentCount, totalPages, analyzedPages } = computeDocumentStats(documentRecords ?? []);
  if (documentCount === 0) return null;

  const areaSubfactors = Object.values(contextSubfactorExplanations ?? {}).reduce(
    (sum, category) => sum + Object.keys(category).length,
    0,
  );
  const activeIssues = propertyIssues?.filter((issue) => !issue.resolved) ?? [];
  const inspectionSurfaces = activeIssues.map((issue) => ({
    title: issue.title,
    category: issue.category,
    description: issue.description,
    urgency: issue.urgency,
    costEstimateMin: issue.costEstimateMin,
    costEstimateMax: issue.costEstimateMax,
    projectTimelineMinDays: issue.projectTimelineMinDays,
    projectTimelineMaxDays: issue.projectTimelineMaxDays,
    component: issue.component,
    trade: issue.trade,
    reportType: issue.reportType,
  }));
  const hoaReviewSurfaces = hoaData
    ? {
        checklist: hoaData.hoaChecklist.map(({ label, status }) => ({ label, status })),
        demandAndInsurance: hoaData.hoaFindings,
        meetings: hoaData.hoaBoardMinutesFindings?.meetings,
        operatingRules: hoaData.hoaOperatingRulesFindings,
        annualBudget: hoaData.hoaAnnualBudgetFindings,
        environmental: hoaData.hoaEnvironmentalFindings?.risks.map((risk) => ({
          category: risk.category,
          severity: risk.severity,
          title: risk.title,
          shortSummary: risk.shortSummary,
          summary: risk.summary,
          obligations: risk.obligations,
          affectedParties: risk.affectedParties,
          contaminants: risk.contaminants,
          agencies: risk.agencies,
          locations: risk.locations,
          recommendations: risk.recommendations,
          measurements: risk.measurements,
          permitCompliance: risk.permitCompliance,
          recordedReference: risk.recordedReference,
          evidence: risk.evidence,
        })),
      }
    : null;
  const marketReviewSurfaces = marketData
    ? {
        avm: marketData.avm.data,
        comparables: marketData.comparables.data.map(
          ({ attomId: _attomId, ...visibleComparable }) => visibleComparable,
        ),
        trends: marketData.trends.data,
      }
    : null;
  // Issues are reviewed once in The Home and again in Repair Planner, where
  // costs, timing and trade assignments tell a different part of the story.
  const measuredContent = measureReviewContent([
    inspectionSurfaces,
    inspectionSurfaces,
    contextSubfactorExplanations,
    hoaReviewSurfaces,
    marketReviewSurfaces,
  ]);
  const hoaReviewCards =
    (hoaData?.hoaChecklist.length ?? 0) +
    (hoaData?.hoaBoardMinutesFindings?.meetings.length ?? 0) +
    (hoaData?.hoaEnvironmentalFindings?.risks.length ?? 0) +
    (hoaData?.hoaOperatingRulesFindings?.notableRestrictions?.length ?? 0) +
    (hoaData?.hoaAnnualBudgetFindings?.reserveProjection?.length ?? 0);
  const workload: HauserReviewWorkload = {
    reviewableWords: measuredContent.words,
    dataPoints: measuredContent.dataPoints,
    reviewCards:
      8 +
      activeIssues.length * 2 +
      areaSubfactors +
      hoaReviewCards +
      (marketData?.comparables.data.length ?? 0),
    actionableFindings:
      activeIssues.length + (hoaData?.hoaEnvironmentalFindings?.risks.length ?? 0),
  };

  const hasPageData = totalPages > 0;
  const hasAnalyzedPages = analyzedPages > 0;
  const hauserRange = hasAnalyzedPages ? formatMinutesRange(...estimateHauserReviewMinutes(workload)) : null;

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderTop: '1px solid var(--tx-line)',
        px: '20px',
        pt: '10px',
        pb: '25px',
      }}>
      <Box
        component="button"
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          borderRadius: '8px',
          border: 0,
          background: 'transparent',
          px: '4px',
          py: '4px',
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
        }}>
        <BarChartIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...STAT_LABEL_STYLE, color: '#aaa' }}>
            Due diligence efficiency
          </Typography>
          <Typography sx={{ fontSize: 10, color: '#666', mt: '1px' }}>
            {hasPageData
              ? `${totalPages} pages${hauserRange ? ` · ${hauserRange}` : ''}`
              : `${documentCount} document${documentCount === 1 ? '' : 's'} on file`}
          </Typography>
        </Box>
        {expanded ? (
          <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
        ) : (
          <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
        )}
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: '4px', pt: '4px' }}>
          {hasPageData ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                mt: '20px',
              }}>
              <StatTile value={documentCount} label="Docs" />
              <StatTile
                value={hasAnalyzedPages ? analyzedPages : totalPages}
                label={`${totalPages} pages`}
              />
              <AnalyzedDonutTile
                percent={hasAnalyzedPages ? Math.round((analyzedPages / totalPages) * 100) : 0}
              />
            </Box>
          ) : (
            <StatRow label="Documents" value={`${documentCount}`} />
          )}
          {hasPageData && hasAnalyzedPages && (
            <EfficiencyComparison totalPages={totalPages} workload={workload} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default PropertyStatsFooter;
